/**
 * App state. Classic React context — no state library, matching the project's
 * "no extra dependencies" rule (CLAUDE.md §4).
 *
 * THE DERIVED KEY NEVER LEAVES MEMORY. The passphrase lives in this provider's
 * state for the session and is written nowhere: not localStorage (§10 forbids
 * it outright), not IndexedDB, not a cookie. Closing the tab loses it, which is
 * the intended behaviour for something that decrypts a company's books.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { Snapshot } from "@tally/shared";

import { loadSnapshot } from "./sync/db.js";
import { freshness, sync, syncNow } from "./sync/sync.js";
import type { Freshness, SyncOutcome } from "./sync/sync.js";
import { indexGroups } from "./derive/groups.js";
import type { GroupIndex } from "./derive/groups.js";
import { DEFAULT_FLAGS, loadFlags, saveFlag } from "./features.js";
import type { FeatureId, Flags } from "./features.js";
import { clearLock, lockState } from "./lock.js";
import type { LockState } from "./lock.js";
import {
  loadConnections,
  removeConnection as removeConnectionEntry,
  saveConnectionEntry,
  switchTo,
} from "./connections.js";

export type { Connection } from "./connections.js";
import type { Connection } from "./connections.js";

export type Status =
  | { kind: "loading" }
  | { kind: "needs-setup" }
  /** a PIN is set and can supply the passphrase without the user typing it */
  | { kind: "needs-pin"; attemptsLeft: number }
  | { kind: "needs-passphrase" }
  | { kind: "ready" }
  | { kind: "error"; message: string };

type AppState = {
  status: Status;
  connection: Connection | null;
  snapshot: Snapshot | null;
  groups: GroupIndex | null;
  /** the snapshot's own generation time, which is what freshness is measured from */
  generatedAt: string | null;
  fresh: Freshness | null;
  syncing: boolean;
  message: string | null;
  offline: boolean;
  /** today, as the snapshot's calendar sees it — every report needs an "as of" */
  asOf: string;

  /** non-null while an on-demand sync is in flight */
  pulling: string | null;

  /** per-device feature switches; every risky feature checks its own */
  flags: Flags;
  setFlag: (id: FeatureId, on: boolean) => Promise<void>;

  /** whether a PIN is set on this device, and how many tries remain */
  lockInfo: LockState;
  /** the live passphrase, exposed ONLY so the PIN setup screen can wrap it */
  sessionPassphrase: string | null;
  refreshLockInfo: () => Promise<void>;
  removeLock: () => Promise<void>;
  /** skip the PIN and fall back to typing the passphrase */
  usePassphraseInstead: () => void;

  /** every company configured on this phone */
  companies: Connection[];
  saveConnection: (connection: Connection) => Promise<void>;
  switchCompany: (companyId: string) => Promise<void>;
  forgetCompany: (companyId: string) => Promise<void>;
  unlock: (passphrase: string) => Promise<void>;
  refresh: () => Promise<void>;
  /** ask the laptop to extract and push, then wait for it */
  syncNow: () => Promise<void>;
  lock: () => void;
};

const Ctx = createContext<AppState | null>(null);

/**
 * Accept connection settings from the URL, so a phone can be set up by tapping
 * a link instead of typing a 43-character token into a password field it cannot
 * display. Mistyping that is the single most likely way to fail at first run.
 *
 *   https://<app>/?worker=https://...workers.dev&token=...&company=sitsmart
 *
 * The query string is stripped immediately afterwards so the token does not sit
 * in the address bar, get screenshotted, or survive in a history entry. It is
 * NOT a passphrase: it authorises downloading ciphertext and decrypts nothing.
 */
async function adoptSettingsFromUrl(): Promise<void> {
  if (typeof window === "undefined" || !window.location.search) return;

  const params = new URLSearchParams(window.location.search);
  const baseUrl = params.get("worker")?.trim();
  const readToken = params.get("token")?.trim();
  const companyId = params.get("company")?.trim();

  // All three or nothing — a half-configured app is worse than an empty form.
  if (!baseUrl || !readToken || !companyId) return;

  // ADDS the company rather than replacing whatever was here. Opening a second
  // company's setup link on a phone that already has one is how a trader with
  // two firms sets up the second — overwriting would silently unconfigure the
  // first, and the link is the only place the read token exists.
  await saveConnectionEntry({ baseUrl, readToken, companyId });

  window.history.replaceState({}, "", window.location.pathname + window.location.hash);
}

export function useApp(): AppState {
  const value = useContext(Ctx);
  if (!value) throw new Error("useApp used outside AppProvider");
  return value;
}

export function AppProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>({ kind: "loading" });
  const [connection, setConnection] = useState<Connection | null>(null);
  const [companies, setCompanies] = useState<Connection[]>([]);
  const [passphrase, setPassphrase] = useState<string | null>(null);
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [pulling, setPulling] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [offline, setOffline] = useState(false);
  const [tick, setTick] = useState(0);
  const [flags, setFlags] = useState<Flags>(DEFAULT_FLAGS);
  const [lockInfo, setLockInfo] = useState<LockState>({ kind: "none" });

  /* Load saved connection settings, and any snapshot already on the device. */
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      await adoptSettingsFromUrl();

      // Read the switches before anything can render. A feature that flickers
      // on for one frame before its flag loads has already leaked the thing
      // the flag exists to hold back.
      const savedFlags = await loadFlags();
      if (!cancelled) setFlags(savedFlags);

      const { list, active } = await loadConnections();

      if (cancelled) return;

      if (!active) {
        setStatus({ kind: "needs-setup" });
        return;
      }

      setCompanies(list);
      setConnection(active);
      const companyId = active.companyId;

      // Showing the cached snapshot before the passphrase is entered would mean
      // it had been stored decrypted. It has not — this only checks existence.
      const stored = await loadSnapshot(companyId);
      if (cancelled) return;

      // A PIN only stands in for the passphrase if the feature is still on.
      // Switching the lock off in Settings must not leave the PIN screen as the
      // way in, or the switch would be a lie.
      const lock = await lockState();
      if (cancelled) return;
      setLockInfo(lock);
      setStatus(
        lock.kind === "set" && savedFlags.appLock
          ? { kind: "needs-pin", attemptsLeft: lock.attemptsLeft }
          : { kind: "needs-passphrase" },
      );
      if (stored) setGeneratedAt(stored.generatedAt);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  /* Re-render the "synced N minutes ago" label once a minute. */
  useEffect(() => {
    const timer = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(timer);
  }, []);

  const applyOutcome = useCallback((outcome: SyncOutcome) => {
    switch (outcome.status) {
      case "up-to-date":
      case "updated":
        setSnapshot(outcome.snapshot);
        setGeneratedAt(outcome.generatedAt);
        setOffline(false);
        setMessage(outcome.status === "updated" ? "Updated." : null);
        setStatus({ kind: "ready" });
        break;
      case "offline":
        setOffline(true);
        setMessage(outcome.message);
        if (outcome.snapshot) {
          setSnapshot(outcome.snapshot);
          setGeneratedAt(outcome.snapshot.generatedAt);
          setStatus({ kind: "ready" });
        } else {
          setStatus({ kind: "error", message: outcome.message });
        }
        break;
      case "error":
        if (outcome.kind === "passphrase") {
          setPassphrase(null);
          setStatus({ kind: "needs-passphrase" });
          setMessage(outcome.message);
        } else {
          setStatus({ kind: "error", message: outcome.message });
        }
        break;
    }
  }, []);

  const runSync = useCallback(
    async (conn: Connection, pass: string) => {
      setSyncing(true);
      setMessage(null);
      try {
        applyOutcome(await sync(conn, conn.companyId, pass));
      } finally {
        setSyncing(false);
      }
    },
    [applyOutcome],
  );

  const saveConnection = useCallback(async (next: Connection) => {
    const { list, active } = await saveConnectionEntry(next);
    setCompanies(list);
    setConnection(active);
    setStatus({ kind: "needs-passphrase" });
  }, []);

  /**
   * Switch companies without asking for the passphrase again.
   *
   * The agent encrypts every company it syncs with ONE passphrase, so the
   * session key opens all of them. If a company was added from a different
   * agent the decrypt fails and `applyOutcome` drops back to the passphrase
   * prompt on its own — the failure path already exists and is correct.
   */
  const switchCompany = useCallback(
    async (companyId: string) => {
      const { list, active } = await switchTo(companyId);
      setCompanies(list);
      if (!active) return;

      setConnection(active);
      setSnapshot(null);
      setGeneratedAt(null);
      setMessage(null);

      if (passphrase) await runSync(active, passphrase);
      else setStatus({ kind: "needs-passphrase" });
    },
    [passphrase, runSync],
  );

  const forgetCompany = useCallback(
    async (companyId: string) => {
      const { list, active } = await removeConnectionEntry(companyId);
      setCompanies(list);

      if (!active) {
        setConnection(null);
        setSnapshot(null);
        setStatus({ kind: "needs-setup" });
        return;
      }
      if (connection?.companyId === companyId) await switchCompany(active.companyId);
    },
    [connection, switchCompany],
  );

  const unlock = useCallback(
    async (candidate: string) => {
      if (!connection) return;
      setPassphrase(candidate);
      await runSync(connection, candidate);
    },
    [connection, runSync],
  );

  const refresh = useCallback(async () => {
    if (!connection || !passphrase) return;
    await runSync(connection, passphrase);
  }, [connection, passphrase, runSync]);

  /**
   * Ask the laptop to sync, then wait for it.
   *
   * `pulling` carries a human-readable phase rather than a boolean, because a
   * wait of up to two minutes with a spinner and no explanation reads as broken.
   */
  const syncNowAction = useCallback(async () => {
    if (!connection || !passphrase || pulling) return;
    setMessage(null);
    setPulling("Asking the laptop…");
    try {
      const outcome = await syncNow(connection, connection.companyId, passphrase, (p) => {
        if (p.phase === "asking") setPulling("Asking the laptop…");
        else if (p.phase === "downloading") setPulling("Downloading…");
        else setPulling(`Waiting for the laptop… ${p.secondsWaited}s`);
      });
      applyOutcome(outcome);
      if (outcome.status === "updated") setMessage("Synced from the laptop just now.");
    } finally {
      setPulling(null);
    }
  }, [connection, passphrase, pulling, applyOutcome]);

  const refreshLockInfo = useCallback(async () => {
    setLockInfo(await lockState());
  }, []);

  const removeLock = useCallback(async () => {
    await clearLock();
    setLockInfo({ kind: "none" });
  }, []);

  const usePassphraseInstead = useCallback(() => {
    setStatus({ kind: "needs-passphrase" });
  }, []);

  const setFlag = useCallback(async (id: FeatureId, on: boolean) => {
    setFlags((current) => ({ ...current, [id]: on }));
    await saveFlag(id, on);

    // Switching the app lock off deletes the stored passphrase outright. Merely
    // ignoring it would leave an encrypted copy of the passphrase on a device
    // whose owner believes they have removed it.
    if (id === "appLock" && !on) {
      await clearLock();
      setLockInfo({ kind: "none" });
    }
  }, []);

  const lock = useCallback(() => {
    setPassphrase(null);
    setSnapshot(null);
    setStatus({ kind: "needs-passphrase" });
  }, []);

  const groups = useMemo(() => (snapshot ? indexGroups(snapshot) : null), [snapshot]);

  const fresh = useMemo(() => {
    void tick; // recomputed every minute
    return generatedAt ? freshness(generatedAt, new Date()) : null;
  }, [generatedAt, tick]);

  // Reports are "as of" today, not as of the snapshot — ageing must keep moving
  // while the laptop is off, or an overdue bill silently stops ageing.
  const asOf = useMemo(() => {
    void tick;
    return new Date().toISOString().slice(0, 10);
  }, [tick]);

  const value: AppState = {
    status,
    connection,
    snapshot,
    groups,
    generatedAt,
    fresh,
    syncing,
    pulling,
    message,
    offline,
    asOf,
    companies,
    switchCompany,
    forgetCompany,
    flags,
    setFlag,
    lockInfo,
    sessionPassphrase: passphrase,
    refreshLockInfo,
    removeLock,
    usePassphraseInstead,
    saveConnection,
    unlock,
    refresh,
    syncNow: syncNowAction,
    lock,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
